import { useGraphqlQuery } from "graphql-mock"
import { useHasValueChanged } from "hooks"
import { ErrorFallback } from "materia"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { backgroundImageStyle, modifiers } from "ui-tools"
import { getIsVideoUrl } from "../../../dataUtilities/getIsVideoUrl"
import type { Data as JourneyProduct } from "../JourneyCard/gql"
import { JourneyProducts } from "../JourneyProducts"
import { JourneyUnlockedModal } from "../JourneyUnlockedModal"
import { type Data, defaultJourney, query } from "./gql"
import { JourneyHeader } from "./JourneyHeader"
import { JourneyInfo } from "./JourneyInfo"
import { JourneySkeleton } from "./JourneySkeleton"

import s from "./Journey.module.scss"

type JourneyConnectedProps = { journeyId: string; mainJourneyOnly?: boolean }
export const JourneyConnected = ({
  journeyId,
  mainJourneyOnly = false,
}: JourneyConnectedProps) => {
  const { data, isPending, error } = useGraphqlQuery(query, { id: journeyId })

  const journey = data?.productCategory ?? defaultJourney

  return (
    <Journey
      key={journeyId}
      isLoading={isPending}
      error={error}
      journey={
        mainJourneyOnly
          ? { ...journey, progress: journey.mainJourneyProgress }
          : journey
      }
    />
  )
}

type JourneyProps = {
  isLoading: boolean
  error: unknown
  journey: Data
}
export const Journey = ({ isLoading, error, journey }: JourneyProps) => {
  const { t } = useTranslation()

  const {
    backgroundImageUrl,
    description,
    descriptionImageUrl,
    id,
    lockedBy,
    isLocked,
    productCategorizations: { nodes },
    progress,
    title,
  } = journey

  const products = nodes.reduce((acc: JourneyProduct[], node) => {
    if (node.product !== null) acc.push(node.product)
    return acc
  }, [])

  const [showJourneyInfo, setShowJourneyInfo] = useState(false)
  const hasJourneyInfo = Boolean(description || isLocked)

  const backgroundURL = backgroundImageUrl ?? ""
  const isVideo = getIsVideoUrl(backgroundURL)

  const { certifiedCount, certifiedMax, certified: isCertified } = progress
  const progressPercentage = certifiedMax
    ? Math.round((100 * certifiedCount) / certifiedMax)
    : 0

  const lockedByJourneys: string[] = lockedBy.nodes
    .map((journey) => journey.title)
    .filter((title) => title !== null)

  const enableAnimations = useHasValueChanged(isLocked)

  if (isLoading) return <JourneySkeleton />

  if (error)
    return (
      <div className={s.error}>
        <ErrorFallback
          title={t("error.GENERIC")}
          message={t("error.JOURNEY_LIST_ERROR")}
          error={error}
        />
      </div>
    )

  return (
    <>
      <div
        className={modifiers(s, "journey", { isLocked, enableAnimations })}
        style={isVideo ? {} : backgroundImageStyle(backgroundURL)}
      >
        {isVideo && <VideoBackground url={backgroundURL} />}

        <JourneyHeader>
          <JourneyHeader.Status
            progressPercentage={progressPercentage}
            isCertified={isCertified}
            isLocked={isLocked}
            lockedByJourneys={lockedByJourneys}
          />

          <JourneyHeader.Title title={title} />

          {hasJourneyInfo && (
            <JourneyInfo.Button
              icon="Info"
              title="Open journey info"
              onClick={() => setShowJourneyInfo(true)}
            />
          )}
        </JourneyHeader>

        {showJourneyInfo && (
          <JourneyInfo
            avatar={descriptionImageUrl}
            description={description}
            isLocked={isLocked}
            lockedBy={lockedByJourneys}
            onClose={() => setShowJourneyInfo(false)}
          />
        )}

        <JourneyProducts products={products} isLocked={isLocked} />
      </div>

      <JourneyUnlockedModal key={id} data={journey} />
    </>
  )
}

/**
 * Video background component
 * The height of the journey container is flexible, based on the number of products.
 * We do not know how many videos we need to render to fill the container.
 * To resolve this we utilize a recursive component that renders videos until the container is filled.
 * TODO: If journey height is predictable based on products, we can optimize the recursion and do calculations upfront instead of on each video load.
 */
const VideoBackground = ({ url }: { url: string }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    <div ref={containerRef} className={s.journey__video}>
      <VideoStackRecursive
        url={url}
        containerRef={containerRef}
        videoIndex={1}
      />
    </div>
  )
}

const VideoStackRecursive = ({
  url,
  containerRef,
  videoIndex,
}: {
  url: string
  containerRef: React.RefObject<HTMLDivElement>
  videoIndex: number
}) => {
  const { videoRef, shouldRenderNext } = useVideoStack(containerRef, videoIndex)
  return (
    <>
      <video
        ref={videoRef}
        src={url}
        autoPlay
        loop
        muted
        playsInline // without this, iPhone can open video in fullscreen and we don't want that
        preload="auto"
        aria-hidden
        tabIndex={-1}
      />
      {shouldRenderNext && (
        <VideoStackRecursive
          url={url}
          containerRef={containerRef}
          videoIndex={videoIndex + 1}
        />
      )}
    </>
  )
}

const useVideoStack = (
  containerRef: React.RefObject<HTMLDivElement>,
  depth: number
) => {
  const MAX_RECURSION_DEPTH = 10
  const videoRef = useRef<HTMLVideoElement>(null)
  const [shouldRenderNext, setShouldRenderNext] = useState(false)

  const handleMetadataLoaded = useCallback(() => {
    const container = containerRef.current
    const video = videoRef.current

    if (container && video) {
      const containerBottom = container.getBoundingClientRect().bottom
      const videoBottom = video.getBoundingClientRect().bottom
      const isDepthLimitReached = depth > MAX_RECURSION_DEPTH

      if (videoBottom < containerBottom && !isDepthLimitReached) {
        setShouldRenderNext(true)
      }
    }
  }, [containerRef, depth])

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.addEventListener("loadedmetadata", handleMetadataLoaded, {
        once: true,
      })
    }

    return () => {
      if (video)
        video.removeEventListener("loadedmetadata", handleMetadataLoaded)
    }
  }, [handleMetadataLoaded])

  return { videoRef, shouldRenderNext }
}
